import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Agent-owned schedules — "run this background agent on this cron". Each row
 * is mirrored to a Temporal Schedule on the backend; runs it fires carry
 * `triggerKind: "schedule"` and `triggerId: <schedule id>`.
 */

// --- Response schema ---

export const AgentScheduleResponseSchema = z.object({
  id: z.uuid(),
  agentDefinitionId: z.uuid(),
  description: z.string().nullable(),
  /** Five-field cron, read in `timezone`. */
  cronExpression: z.string(),
  timezone: z.string(),
  inputData: z.record(z.string(), z.unknown()),
  isEnabled: z.boolean(),
  /**
   * When the cron next fires, computed for display — null when the schedule
   * is disabled or its expression cannot be read. Temporal owns real firing.
   */
  nextRunAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// --- Input schemas ---

const AgentScheduleWriteShape = {
  description: z.string().trim().max(500).nullable().optional(),
  cronExpression: z.string().trim().min(1).max(120),
  timezone: z.string().trim().min(1).max(64),
  inputData: z.record(z.string(), z.unknown()).optional(),
  isEnabled: z.boolean().optional(),
} as const;

export const CreateAgentScheduleInputSchema = z.object({
  agentDefinitionId: z.uuid(),
  ...AgentScheduleWriteShape,
});

/** All fields optional on update; only the ones present change. */
export const UpdateAgentScheduleInputSchema = z.object({
  id: z.uuid(),
  ...AgentScheduleWriteShape,
  cronExpression: AgentScheduleWriteShape.cronExpression.optional(),
  timezone: AgentScheduleWriteShape.timezone.optional(),
});

export const ListAgentSchedulesInputSchema = z.object({
  agentDefinitionId: z.uuid(),
});

const AgentScheduleIdInputSchema = z.object({ id: z.uuid() });

// --- Contracts ---

export const agentSchedulesContracts = {
  list: oc
    .input(ListAgentSchedulesInputSchema)
    .output(z.array(AgentScheduleResponseSchema))
    .route({
      method: "GET",
      summary: "List a background agent's schedules",
      description:
        "Lists the cron schedules of one background agent, oldest first, with timezone, input data, enabled flag and next run time.",
    }),

  create: oc
    .input(CreateAgentScheduleInputSchema)
    .output(AgentScheduleResponseSchema)
    .route({
      method: "POST",
      summary: "Schedule a background agent",
      description:
        "Adds a cron schedule (expression, timezone, optional input data) that launches runs of a background agent automatically; registers it with the scheduler.",
    }),

  update: oc
    .input(UpdateAgentScheduleInputSchema)
    .output(AgentScheduleResponseSchema)
    .route({
      method: "PUT",
      summary: "Update an agent schedule",
      description:
        "Changes a background agent schedule's cron expression, timezone, label, input data or enabled flag, and updates the registered scheduler entry.",
    }),

  delete: oc
    .input(AgentScheduleIdInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "DELETE",
      summary: "Delete an agent schedule",
      description:
        "Deletes a background agent's cron schedule and removes it from the scheduler so no further runs launch from it.",
    }),
} as const;

// --- Type exports ---

export type AgentScheduleResponse = z.infer<typeof AgentScheduleResponseSchema>;
export type CreateAgentScheduleInput = z.infer<
  typeof CreateAgentScheduleInputSchema
>;
export type UpdateAgentScheduleInput = z.infer<
  typeof UpdateAgentScheduleInputSchema
>;
export type ListAgentSchedulesInput = z.infer<
  typeof ListAgentSchedulesInputSchema
>;
